<template>
  <!-- 通用虚拟滚动列表：仅渲染可视区域内的项 -->
  <div class="virtual-list" ref="container" @scroll.passive="onScroll">
    <div class="virtual-list__spacer" :style="{ height: totalHeight + 'px' }"></div>
    <div class="virtual-list__visible" :style="{ transform: 'translateY(' + offsetY + 'px)' }">
      <div
        v-for="item in visibleItems"
        :key="getItemKey(item)"
      >
        <slot name="item" :item="item" :index="getItemIndex(item)" />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * VirtualList - 虚拟滚动列表组件
 * 仅渲染可视区域+缓冲区内的项
 *
 * Props:
 *   items        - 数据源数组
 *   getItemHeight - 函数，接收 item 返回该项高度(px)，默认返回72
 *   buffer       - 上下缓冲区项数，默认5
 *   itemKey      - key 字段名，默认 'id'
 */
export default {
  name: 'VirtualList',
  props: {
    items: { type: Array, default: () => [] },
    /** 函数: (item) => height(px)，不传则统一72px */
    getItemHeight: { type: Function, default: null },
    buffer: { type: Number, default: 5 },
    itemKey: { type: String, default: 'id' }
  },
  data() {
    return {
      scrollTop: 0,
      containerHeight: 0,
      /** 预计算的累积高度数组，用于二分查找 */
      offsets: [],
      /** 缓存每项高度 */
      heights: [],
      /** 高度缓存版本号，items变化时递增 */
      heightVersion: 0
    }
  },
  computed: {
    totalHeight() {
      return this.offsets.length > 0 ? this.offsets[this.offsets.length - 1] : 0
    },
    startIndex() {
      if (this.offsets.length <= 1) return 0
      // 二分查找 scrollTop 对应的起始索引
      let lo = 0
      let hi = this.offsets.length - 1
      while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2)
        if (this.offsets[mid] <= this.scrollTop) lo = mid + 1
        else hi = mid
      }
      return Math.max(0, lo - 1 - this.buffer)
    },
    endIndex() {
      if (this.offsets.length <= 1) return this.items.length
      const targetBottom = this.scrollTop + this.containerHeight
      let lo = this.startIndex
      let hi = this.offsets.length - 1
      while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2)
        if (this.offsets[mid] < targetBottom) lo = mid + 1
        else hi = mid
      }
      return Math.min(this.items.length, lo + this.buffer)
    },
    visibleItems() {
      return this.items.slice(this.startIndex, this.endIndex)
    },
    offsetY() {
      return this.startIndex > 0 ? this.offsets[this.startIndex - 1] : 0
    }
  },
  methods: {
    getItemKey(item) {
      return item[this.itemKey]
    },
    getItemIndex(item) {
      return this.items.indexOf(item)
    },
    onScroll() {
      if (!this.$refs.container) return
      this.scrollTop = this.$refs.container.scrollTop
    },
    measureContainer() {
      if (this.$refs.container) {
        this.containerHeight = this.$refs.container.clientHeight
      }
    },
    /** 重新计算所有项的累积偏移 */
    recalcOffsets() {
      const fn = this.getItemHeight || (() => 72)
      const h = this.items.map(fn)
      this.heights = h
      this.offsets = []
      let sum = 0
      for (let i = 0; i < h.length; i++) {
        sum += h[i]
        this.offsets.push(sum)
      }
      this.heightVersion++
    },
    scrollToIndex(index) {
      if (index <= 0) {
        this.$refs.container.scrollTop = 0
        return
      }
      this.$nextTick(() => {
        const top = this.offsets[Math.min(index - 1, this.offsets.length - 1)] || 0
        if (this.$refs.container) {
          this.$refs.container.scrollTop = top
        }
      })
    }
  },
  watch: {
    items: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.recalcOffsets()
          this.measureContainer()
        })
      }
    }
  },
  mounted() {
    this.recalcOffsets()
    this.measureContainer()
    if (window.ResizeObserver) {
      this._resizeObserver = new ResizeObserver(() => {
        this.measureContainer()
      })
      this._resizeObserver.observe(this.$refs.container)
    }
  },
  beforeDestroy() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
    }
  }
}
</script>

<style lang="scss" scoped>
.virtual-list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;

  &__spacer {
    pointer-events: none;
  }

  &__visible {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  }
}
</style>
